import { inject, Injectable } from '@angular/core';
import { HierarchicalItem } from '@web-app/app/interfaces/api/hierarchical';
import {
  DeploymentDetails,
  DeploymentEdit,
  DeploymentNotificationDetails,
  DeploymentNotificationEdit,
  DeploymentNotificationsApi,
  DeploymentRemoteSetting,
  DeploymentsApi as DeploymentsClient,
  DeploymentSummary,
  DeploymentThresholdsApi,
  DeploymentThresholdsEdit,
} from '@web-app/app/services/typed-api';
import { Hierarchical } from '@web-app/app/utils/hierarchical';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DeploymentsApi {
  private readonly deploymentsApi = inject(DeploymentsClient);
  private readonly thresholdsApi = inject(DeploymentThresholdsApi);
  private readonly notificationsApi = inject(DeploymentNotificationsApi);

  public createDeployment(deploymentData: DeploymentEdit): Observable<DeploymentDetails> {
    return this.deploymentsApi.createDeployment(deploymentData);
  }

  public editDeployment(deploymentData: DeploymentEdit, deploymentId: number): Observable<void> {
    return this.deploymentsApi.editDeployment(deploymentId, deploymentData);
  }

  /** Creates the thresholds when there's no `thresholdId` yet, otherwise edits them. */
  public editDeploymentThresholds(thresholdData: DeploymentThresholdsEdit, thresholdId: number | null): Observable<void> {
    const request = thresholdId == null
      ? this.thresholdsApi.createDeploymentThreshold(thresholdData)
      : this.thresholdsApi.editDeploymentThreshold(thresholdId, thresholdData);
    return request.pipe(map(() => undefined));
  }

  public syncThresholds(deploymentId: number): Observable<void> {
    return this.deploymentsApi.syncDeploymentThresholds(deploymentId).pipe(map(() => undefined));
  }

  public deleteDeployment(deploymentId: number): Observable<void> {
    return this.deploymentsApi.deleteDeployment(deploymentId);
  }

  public setRemoteDeploymentStatus(deploymentData: DeploymentRemoteSetting, deploymentId: number): Observable<void> {
    return this.deploymentsApi.editDeploymentRemoteSetting(deploymentId, deploymentData);
  }

  /** The tenant's deployments, sorted parent-first with their hierarchy level. */
  public getDeploymentTree(tenant: number): Observable<HierarchicalItem<DeploymentSummary>[]> {
    return this.deploymentsApi.getDeploymentTree(tenant).pipe(map(deployments => Hierarchical.sortByLevel(deployments ?? [])));
  }

  public changeUserNotifications(notificationData: DeploymentNotificationEdit): Observable<void> {
    return this.notificationsApi.editDeploymentNotification(notificationData.deploymentId, notificationData);
  }

  public removeUserNotification(notificationId: number): Observable<void> {
    return this.notificationsApi.deleteDeploymentNotification(notificationId);
  }

  public getUserNotifications(deploymentId: number): Observable<DeploymentNotificationDetails[]> {
    return this.notificationsApi.getDeploymentNotificationDetails(deploymentId);
  }
}
